import React from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Switch, TextField,} from '@mui/material';
import {useAppointmentManagementGraphQL} from '../../service/useAppointmentGraphQL';
import {useTranslation} from 'react-i18next';

const PRICE_RE = /^\d+([.,]\d{1,2})?$/;

function normalizePrice(price) {
    if (price == null || price === '') return '';
    // We already validate with PRICE_RE, so convert comma to dot and return as STRING
    return String(price).trim().replace(',', '.'); // GraphQL schema expects String! for price
}

function intOr(value, def = 0) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : def;
}

export default function ServiceEditModal({open, onClose, initial, onSaved}) {
    const isEdit = !!(initial && initial.uuid);
    const {t} = useTranslation();
    const initialForm = {
        uuid: initial?.uuid || undefined,
        name: initial?.name || '',
        description: initial?.description || '',
        durationMinutes: initial?.durationMinutes ?? '',
        price: initial?.price ?? '',
        species: initial?.species ?? '',
        bufferBeforeMinutes: initial?.bufferBeforeMinutes ?? 0,
        bufferAfterMinutes: initial?.bufferAfterMinutes ?? 0,
        active: initial?.active ?? true,
    }
    const [form, setForm] = React.useState(() => (initialForm));
    const [errors, setErrors] = React.useState({});
    const {useUpsertService} = useAppointmentManagementGraphQL();
    const [upsertService, {loading}] = useUpsertService();

    React.useEffect(() => {
        if (open) {
            setForm(initialForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial]);

    const onChange = (e) => {
        const {name, value} = e.target;
        setForm((f) => ({...f, [name]: value}));
    };

    const onToggleActive = (e) => setForm((f) => ({...f, active: e.target.checked}));

    const validate = () => {
        const v = {};
        const name = (form.name || '').trim();
        if (name.length < 2) v.name = t('minTwoChars');
        const dur = intOr(form.durationMinutes);
        if (!Number.isFinite(dur) || dur < 5 || dur > 480) v.durationMinutes = t('durationRange');
        const bb = intOr(form.bufferBeforeMinutes, 0);
        if (bb < 0 || bb > 120) v.bufferBeforeMinutes = t('bufferRange');
        const ba = intOr(form.bufferAfterMinutes, 0);
        if (ba < 0 || ba > 120) v.bufferAfterMinutes = t('bufferRange');
        const priceStr = String(form.price ?? '').trim();
        if (!priceStr) v.price = t('required');
        else if (!PRICE_RE.test(priceStr)) v.price = t('invalidAmount');
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        const input = {
            name: form.name.trim(),
            description: form.description?.trim() || '',
            durationMinutes: intOr(form.durationMinutes),
            price: normalizePrice(form.price),
            active: !!form.active,
            species: form.species || null,
            bufferBeforeMinutes: intOr(form.bufferBeforeMinutes, 0),
            bufferAfterMinutes: intOr(form.bufferAfterMinutes, 0),
        };
        if (isEdit) input.uuid = form.uuid;
        try {
            const {data} = await upsertService({variables: {input}});
            onSaved?.(data?.upsertService || null);
            onClose?.();
        } catch (e) {
            // keep open; surface error near price as a generic spot if no better place
            setErrors((prev) => ({...prev, __server: t('saveFailedTryAgain')}));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? t('editServiceTitle') : t('newServiceTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{mt: 1}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label={t('name')}
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                fullWidth
                                required
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('description')}
                                name="description"
                                value={form.description}
                                onChange={onChange}
                                fullWidth
                                multiline
                                minRows={2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                type="number"
                                label={t('durationMinutesLabel')}
                                name="durationMinutes"
                                value={form.durationMinutes}
                                onChange={onChange}
                                fullWidth
                                required
                                error={!!errors.durationMinutes}
                                helperText={errors.durationMinutes}
                                inputProps={{min: 5, max: 480}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('priceEuro')}
                                name="price"
                                value={form.price}
                                onChange={onChange}
                                fullWidth
                                required
                                error={!!errors.price}
                                helperText={errors.price}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('animalType')}
                                name="species"
                                value={form.species}
                                onChange={onChange}
                            >
                                <MenuItem value="">{t('allAnimals')}</MenuItem>
                                <MenuItem value="CAT">{t('cat')}</MenuItem>
                                <MenuItem value="DOG">{t('dog')}</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Switch checked={!!form.active} onChange={onToggleActive}/>}
                                label={t('active')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                type="number"
                                label={t('bufferBefore')}
                                name="bufferBeforeMinutes"
                                value={form.bufferBeforeMinutes}
                                onChange={onChange}
                                fullWidth
                                error={!!errors.bufferBeforeMinutes}
                                helperText={errors.bufferBeforeMinutes}
                                inputProps={{min: 0, max: 120}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                type="number"
                                label={t('bufferAfter')}
                                name="bufferAfterMinutes"
                                value={form.bufferAfterMinutes}
                                onChange={onChange}
                                fullWidth
                                error={!!errors.bufferAfterMinutes}
                                helperText={errors.bufferAfterMinutes}
                                inputProps={{min: 0, max: 120}}
                            />
                        </Grid>
                        {errors.__server && (
                            <Grid item xs={12}>
                                <Box sx={{color: 'error.main', fontSize: 14}}>{errors.__server}</Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading} data-testid="cancel-service-button">{t('cancel')}</Button>
                <Button onClick={onSave} variant="contained" disabled={loading} data-testid="save-service-button">{t('save')}</Button>
            </DialogActions>
        </Dialog>
    );
}
